const MIN_LENGTH = 100;
const MAX_LENGTH = 1000;

const humanizeId = (id) =>
  String(id || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

/** Build label list from flat amenities and grouped amenity definitions. */
export const collectAmenityLabels = (selectedIds = [], catalogs = []) => {
  const map = new Map();

  catalogs.forEach((entry) => {
    if (!entry) return;
    if (entry.id && entry.label) map.set(entry.id, entry.label);
    if (Array.isArray(entry.amenities)) {
      entry.amenities.forEach((a) => {
        if (a?.id) map.set(a.id, a.label || humanizeId(a.id));
      });
    }
  });

  return (Array.isArray(selectedIds) ? selectedIds : [])
    .map((id) => map.get(id) || humanizeId(id))
    .filter(Boolean);
};

const formatIndianPrice = (raw) => {
  const n = parseFloat(String(raw || '').replace(/,/g, ''));
  if (!n || Number.isNaN(n)) return '';
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Crore`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)} Lakh`;
  return `₹${n.toLocaleString('en-IN')}`;
};

const joinList = (items) => {
  const list = items.filter(Boolean);
  if (list.length === 0) return '';
  if (list.length === 1) return list[0];
  if (list.length === 2) return `${list[0]} and ${list[1]}`;
  return `${list.slice(0, -1).join(', ')}, and ${list[list.length - 1]}`;
};

const ensureLength = (text) => {
  let result = text.replace(/\s+/g, ' ').trim();
  if (result.length > MAX_LENGTH) {
    result = result.slice(0, MAX_LENGTH - 3).trim();
    const lastSpace = result.lastIndexOf(' ');
    if (lastSpace > MIN_LENGTH) result = result.slice(0, lastSpace);
    result = `${result}...`;
  }
  if (result.length < MIN_LENGTH) {
    const suffix =
      ' Ideal for families and professionals seeking a ready-to-explore listing with transparent details on the platform.';
    result = `${result}${suffix}`.slice(0, MAX_LENGTH).trim();
  }
  return result;
};

/**
 * Build a property description from add-property form fields (frontend only).
 * @param {object} formData
 * @param {{ amenityLabels?: string[] }} options
 */
export const generatePropertyDescription = (formData, options = {}) => {
  const data = formData || {};
  const amenityLabels = options.amenityLabels || [];
  const statusPhrase = data.status === 'rent' ? 'for rent' : 'for sale';
  const paragraphs = [];

  const title = (data.title || '').trim();
  const propertyType = (data.propertyType || '').trim();

  if (title && propertyType) {
    paragraphs.push(
      `${title} is a ${propertyType} available ${statusPhrase}, presented with complete listing information for interested buyers and tenants.`
    );
  } else if (title) {
    paragraphs.push(
      `${title} is available ${statusPhrase} and includes the key details captured in your listing steps.`
    );
  } else if (propertyType) {
    paragraphs.push(
      `This ${propertyType} is offered ${statusPhrase} with specifications and amenities selected during the listing process.`
    );
  } else {
    paragraphs.push(`This property listing is offered ${statusPhrase} with the details provided in the form.`);
  }

  const locationLine = [data.location, data.state].map((v) => (v || '').trim()).filter(Boolean);
  if (locationLine.length) {
    let locText = `Located in ${joinList(locationLine)}`;
    if ((data.additionalAddress || '').trim()) {
      locText += ` near ${data.additionalAddress.trim()}`;
    }
    locText += ', the property benefits from local connectivity and neighbourhood convenience.';
    paragraphs.push(locText);
  }

  const layoutParts = [];
  if (data.bedrooms) {
    layoutParts.push(`${data.bedrooms} bedroom${String(data.bedrooms) === '1' ? '' : 's'}`);
  }
  if (data.bathrooms) {
    layoutParts.push(`${data.bathrooms} bathroom${String(data.bathrooms) === '1' ? '' : 's'}`);
  }
  if (data.balconies) {
    layoutParts.push(`${data.balconies} balcon${String(data.balconies) === '1' ? 'y' : 'ies'}`);
  }
  if (data.area) {
    layoutParts.push(`${data.area} sq.ft built-up area`);
  }
  if (data.carpetArea) {
    layoutParts.push(`${data.carpetArea} sq.ft carpet area`);
  }
  if (data.floor && data.totalFloors) {
    layoutParts.push(`floor ${data.floor} of ${data.totalFloors}`);
  } else if (data.floor) {
    layoutParts.push(`floor ${data.floor}`);
  }
  if (data.facing) layoutParts.push(`${data.facing} facing`);
  if (data.furnishing) layoutParts.push(`${data.furnishing} furnishing`);
  if (data.age) layoutParts.push(`property age: ${data.age}`);
  if (data.seats) layoutParts.push(`${data.seats} seats`);
  if (data.pricePerSeat) layoutParts.push(`₹${data.pricePerSeat} per seat`);

  if (layoutParts.length) {
    paragraphs.push(`The layout and size include ${joinList(layoutParts)}.`);
  }

  const priceText = formatIndianPrice(data.price);
  const priceBits = [];
  if (priceText) {
    priceBits.push(
      data.status === 'rent'
        ? `Monthly rent is ${priceText}${data.priceNegotiable ? ' (negotiable)' : ''}`
        : `Asking price is ${priceText}${data.priceNegotiable ? ' (negotiable)' : ''}`
    );
  }
  if (data.status === 'rent' && data.depositAmount) {
    priceBits.push(`security deposit ${formatIndianPrice(data.depositAmount)}`);
  }
  if (data.maintenanceCharges) {
    priceBits.push(`maintenance ${formatIndianPrice(data.maintenanceCharges)} per month`);
  }
  if (priceBits.length) {
    paragraphs.push(`${joinList(priceBits)}.`);
  }

  if (data.availableForBachelors && data.status === 'rent') {
    paragraphs.push('This rental is marked as suitable for bachelors.');
  }

  if (amenityLabels.length) {
    const shown = amenityLabels.slice(0, 12);
    const extra = amenityLabels.length > shown.length ? ` and ${amenityLabels.length - shown.length} more` : '';
    paragraphs.push(
      `Amenities and highlights include ${joinList(shown)}${extra}, offering comfort and everyday convenience for occupants.`
    );
  }

  paragraphs.push(
    'Schedule a visit to experience the space in person. Connect through the platform to request more details or arrange a viewing.'
  );

  return ensureLength(paragraphs.join(' '));
};

/**
 * Build an upcoming-project description from AddUpcomingProjectPopup form fields.
 * @param {object} formData
 * @param {{ amenityLabels?: string[], builderName?: string }} options
 */
export const generateUpcomingProjectDescription = (formData, options = {}) => {
  const data = formData || {};
  const amenityLabels = options.amenityLabels || [];
  const builderName = (options.builderName || data.builderName || '').trim();
  const paragraphs = [];

  const projectName = (data.projectName || '').trim();
  const projectType = (data.projectType || '').trim();
  const projectStatus = (data.projectStatus || '').trim().replace(/_/g, ' ');

  if (projectName && projectType) {
    paragraphs.push(
      `${projectName} is an upcoming ${projectType} project${builderName ? ` by ${builderName}` : ''}, currently marked as ${projectStatus || 'under development'} and open for buyer enquiries on the platform.`
    );
  } else if (projectName) {
    paragraphs.push(
      `${projectName} is an upcoming real estate project${builderName ? ` developed by ${builderName}` : ''} with key pre-launch details listed for interested buyers.`
    );
  } else {
    paragraphs.push(
      `This upcoming project listing highlights pre-launch information, configuration options, and amenities for prospective buyers and investors.`
    );
  }

  const locationLine = [data.location, data.area, data.state]
    .map((v) => (v || '').trim())
    .filter(Boolean);
  if (locationLine.length) {
    let locText = `The project is located in ${joinList([...new Set(locationLine)])}`;
    if ((data.fullAddress || '').trim()) {
      locText += ` (${data.fullAddress.trim()})`;
    }
    if ((data.pincode || '').trim()) {
      locText += `, pincode ${data.pincode.trim()}`;
    }
    locText += ', offering convenient access to local infrastructure and neighbourhood facilities.';
    paragraphs.push(locText);
  }

  const configParts = [];
  if (Array.isArray(data.configurations) && data.configurations.length) {
    configParts.push(`configurations: ${joinList(data.configurations)}`);
  }
  if ((data.carpetAreaRange || '').trim()) {
    const range = String(data.carpetAreaRange).trim();
    const withUnit = /\bsq\.?\s*ft\b/i.test(range) ? range : `${range} sq.ft`;
    configParts.push(`carpet area range ${withUnit}`);
  }
  if ((data.numberOfTowers || '').trim()) {
    configParts.push(`${data.numberOfTowers} tower(s)`);
  }
  if ((data.totalUnits || '').trim()) {
    configParts.push(`${data.totalUnits} total units`);
  }
  if ((data.floorsCount || '').trim()) {
    configParts.push(`${data.floorsCount} floors`);
  }
  if (configParts.length) {
    paragraphs.push(`Project scale and layout include ${joinList(configParts)}.`);
  }

  const priceBits = [];
  if ((data.startingPrice || '').trim()) {
    priceBits.push(`starting price ${data.startingPrice.trim()}`);
  }
  if ((data.pricePerSqft || '').trim()) {
    priceBits.push(`approx. ${data.pricePerSqft.trim()} per sq.ft`);
  }
  if ((data.bookingAmount || '').trim()) {
    priceBits.push(`booking amount ${data.bookingAmount.trim()}`);
  }
  if (priceBits.length) {
    paragraphs.push(`Pricing overview: ${joinList(priceBits)}.`);
  }

  const timelineBits = [];
  if ((data.expectedLaunchDate || '').trim()) {
    timelineBits.push(`expected launch ${data.expectedLaunchDate.trim()}`);
  }
  if ((data.expectedPossessionDate || '').trim()) {
    timelineBits.push(`expected possession ${data.expectedPossessionDate.trim()}`);
  }
  if (timelineBits.length) {
    paragraphs.push(`Project timeline: ${joinList(timelineBits)}.`);
  }

  if ((data.reraNumber || '').trim()) {
    paragraphs.push(`RERA registration: ${data.reraNumber.trim()}.`);
  }

  if ((data.projectHighlights || '').trim()) {
    paragraphs.push(data.projectHighlights.trim());
  }
  if ((data.usp || '').trim()) {
    paragraphs.push(`Key USP: ${data.usp.trim()}`);
  }

  if (amenityLabels.length) {
    const shown = amenityLabels.slice(0, 12);
    const extra = amenityLabels.length > shown.length ? ` and ${amenityLabels.length - shown.length} more` : '';
    paragraphs.push(
      `Planned amenities include ${joinList(shown)}${extra}, designed to support modern living and long-term value for residents.`
    );
  }

  paragraphs.push(
    'Register your interest through the platform to receive updates, brochure details, and site-visit coordination from the sales team.'
  );

  return ensureLength(paragraphs.join(' '));
};
